import { useState, useEffect, useCallback } from 'react';
import {
  FileCode,
  Download,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Edit2,
  Save,
  X,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import clsx from 'clsx';
import type { XBRLMapping, XBRLValidationResult, ConsolidationRun } from '@/types';
import { xbrlApi, consolidationApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Main Page ---------------------------------------------------------------

export default function XBRLManagement() {
  const { addNotification, currentPeriod } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [mappings, setMappings] = useState<XBRLMapping[]>([]);
  const [taxonomies, setTaxonomies] = useState<{ id: string; name: string; version: string }[]>([]);
  const [runs, setRuns] = useState<ConsolidationRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [selectedTaxonomy, setSelectedTaxonomy] = useState('');

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<XBRLValidationResult | null>(null);

  // Preview
  const [preview, setPreview] = useState<{ content: string; format: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Editing
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ xbrl_element: '', taxonomy: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Search
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [mappingsRes, taxonomiesRes, runsRes] = await Promise.all([
        xbrlApi.mappings().catch(() => []),
        xbrlApi.taxonomy().catch(() => []),
        consolidationApi.list(currentPeriod ? { period_id: currentPeriod.id } : undefined).catch(() => []),
      ]);
      setMappings(mappingsRes);
      setTaxonomies(taxonomiesRes);
      setRuns(runsRes);
      if (runsRes.length > 0) setSelectedRunId(runsRes[0].id);
      if (taxonomiesRes.length > 0) setSelectedTaxonomy(taxonomiesRes[0].id);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load XBRL data');
    } finally {
      setIsLoading(false);
    }
  }, [currentPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGenerate = async () => {
    if (!selectedRunId) return;
    setGenerating(true);
    try {
      const blob = await xbrlApi.generate({ consolidation_run_id: selectedRunId, taxonomy: selectedTaxonomy });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xbrl_report_${selectedRunId}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      addNotification({ type: 'success', title: 'XBRL Generated', message: 'XBRL file has been generated and downloaded.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Generation Failed', message: apiErr.detail || 'Failed to generate XBRL.' });
    } finally {
      setGenerating(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedRunId) return;
    setValidating(true);
    setValidationResult(null);
    try {
      const result = await xbrlApi.validate({ consolidation_run_id: selectedRunId });
      setValidationResult(result);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Validation Failed', message: apiErr.detail || 'Failed to validate.' });
    } finally {
      setValidating(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedRunId) return;
    try {
      const result = await xbrlApi.preview({ consolidation_run_id: selectedRunId });
      setPreview(result);
      setShowPreview(true);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Preview Failed', message: apiErr.detail || 'Failed to generate preview.' });
    }
  };

  const handleSaveMapping = async (accountCode: string) => {
    setSavingEdit(true);
    try {
      const updated = await xbrlApi.updateMapping(accountCode, editForm);
      setMappings((prev) =>
        prev.map((m) => (m.account_code === accountCode ? { ...m, ...updated } : m)),
      );
      setEditingCode(null);
      addNotification({ type: 'success', title: 'Mapping Updated', message: `Mapping for ${accountCode} has been updated.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Update Failed', message: apiErr.detail || 'Failed to update mapping.' });
    } finally {
      setSavingEdit(false);
    }
  };

  const filteredMappings = mappings.filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      m.account_code.toLowerCase().includes(s) ||
      (m.account_name || '').toLowerCase().includes(s) ||
      m.xbrl_element.toLowerCase().includes(s)
    );
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <FileCode className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <FileCode className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">XBRL Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage taxonomy mappings and generate XBRL reports</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Generation Controls */}
      <div className="card p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Generate XBRL Report</h2>
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Consolidation Run</label>
            <select
              className="select-field text-sm w-56"
              value={selectedRunId}
              onChange={(e) => setSelectedRunId(e.target.value)}
            >
              {runs.length === 0 && <option value="">No runs available</option>}
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.period_name || r.period_id} - {r.status}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Taxonomy</label>
            <select
              className="select-field text-sm w-48"
              value={selectedTaxonomy}
              onChange={(e) => setSelectedTaxonomy(e.target.value)}
            >
              {taxonomies.length === 0 && <option value="">No taxonomies</option>}
              {taxonomies.map((t) => (
                <option key={t.id} value={t.id}>{t.name} v{t.version}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleValidate}
              disabled={validating || !selectedRunId}
              className="btn-secondary text-sm"
            >
              {validating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Validate
            </button>
            <button
              onClick={handlePreview}
              disabled={!selectedRunId}
              className="btn-secondary text-sm"
            >
              <Eye className="w-4 h-4 mr-1.5" />
              Preview
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating || !selectedRunId}
              className="btn-primary text-sm"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Generate & Export
            </button>
          </div>
        </div>
      </div>

      {/* Validation Results */}
      {validationResult && (
        <div className={clsx(
          'card p-5 border-l-4',
          validationResult.is_valid ? 'border-l-green-500' : 'border-l-red-500',
        )}>
          <div className="flex items-center gap-2 mb-3">
            {validationResult.is_valid ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
            <h3 className="text-sm font-semibold text-gray-900">
              Validation {validationResult.is_valid ? 'Passed' : 'Failed'}
            </h3>
          </div>
          {validationResult.errors.length > 0 && (
            <div className="space-y-1 mb-2">
              {validationResult.errors.map((err, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-red-700">
                  <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  {err}
                </div>
              ))}
            </div>
          )}
          {validationResult.warnings.length > 0 && (
            <div className="space-y-1">
              {validationResult.warnings.map((warn, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  {warn}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mapping Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Taxonomy Mappings</h2>
          <input
            type="text"
            placeholder="Search accounts..."
            className="input-field text-sm w-64"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {filteredMappings.length === 0 ? (
          <div className="py-12 text-center">
            <FileCode className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No mappings found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="table-header w-28">Account Code</th>
                  <th className="table-header">Account Name</th>
                  <th className="table-header">XBRL Element</th>
                  <th className="table-header w-32">Taxonomy</th>
                  <th className="table-header w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredMappings.map((mapping) => (
                  <tr key={mapping.account_code} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                    <td className="table-cell font-mono text-sm font-medium text-gray-900">{mapping.account_code}</td>
                    <td className="table-cell text-sm text-gray-700">{mapping.account_name || '\u2014'}</td>
                    <td className="table-cell">
                      {editingCode === mapping.account_code ? (
                        <input
                          className="input-field text-sm w-full"
                          value={editForm.xbrl_element}
                          onChange={(e) => setEditForm({ ...editForm, xbrl_element: e.target.value })}
                          placeholder="XBRL element name"
                        />
                      ) : (
                        <span className="text-sm font-mono text-indigo-700">{mapping.xbrl_element || '\u2014'}</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {editingCode === mapping.account_code ? (
                        <input
                          className="input-field text-sm w-full"
                          value={editForm.taxonomy}
                          onChange={(e) => setEditForm({ ...editForm, taxonomy: e.target.value })}
                          placeholder="Taxonomy"
                        />
                      ) : (
                        <span className="badge bg-indigo-50 text-indigo-700 text-xs">{mapping.taxonomy || '\u2014'}</span>
                      )}
                    </td>
                    <td className="table-cell">
                      {editingCode === mapping.account_code ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleSaveMapping(mapping.account_code)}
                            disabled={savingEdit}
                            className="p-1.5 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            {savingEdit ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-green-600" />
                            ) : (
                              <Save className="w-3.5 h-3.5 text-green-600" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingCode(null)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingCode(mapping.account_code);
                            setEditForm({ xbrl_element: mapping.xbrl_element, taxonomy: mapping.taxonomy });
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">XBRL Preview ({preview.format})</h3>
              <button onClick={() => setShowPreview(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <pre className="text-xs font-mono text-gray-700 bg-gray-50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
                {preview.content}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
